import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import ApiService from 'helpers/apiService';
import { hasAccess } from 'helpers/access';

// Вкладка «Фрод» в Финмониторинге. AML-22 (v68 2026-05-29): подключение
// к реальным заблокированным пользователям через /user/v1/list?status=
// blocked. Раньше — захардкоженные моки FRAUD_CASES.
//
// Источник:
//   GET /user/v1/list?status=blocked&size=200
//   → { data: [...UserDto], total: N, statuses: [...] }
// UserDto.is_enabled === false → заблокирован.
// Дополнительные данные (риск-скор, причина, история операций) приходят
// из связанных полей: aml_data, scoring. Сейчас отображаем то что есть.

interface RawUser {
  userId?: string;
  name?: string;
  email?: string;
  phone?: string | number;
  country?: string;
  is_enabled?: boolean;
  status?: string;
  block_reason?: string;
  block_comment?: string;
  blocked_at?: string;
  scoring?: { score?: number; band?: string } | null;
  aml_data?: Array<{ status?: string; risk?: number }>;
}

interface FraudItem {
  userId: string;
  name: string;
  email: string;
  phone: string;
  country: string;
  reason: string;
  riskScore: number;
  blockedAt: string | null;
  status: 'blocked';
  raw: RawUser;
}

const mapUser = (u: RawUser): FraudItem => {
  const phone = u.phone != null ? String(u.phone) : '';
  const name = (u.name || '').trim() || '—';
  const reason = u.block_reason || u.block_comment || '—';
  const riskScore = u.scoring?.score ?? u.aml_data?.[0]?.risk ?? 0;
  return {
    userId: u.userId || '',
    name,
    email: u.email || '—',
    phone: phone ? `+${phone}` : '—',
    country: u.country || '—',
    reason,
    riskScore,
    blockedAt: u.blocked_at || null,
    status: 'blocked',
    raw: u
  };
};

const getRiskColor = (score: number) => {
  if (score >= 90) return 'danger';
  if (score >= 75) return 'warning';
  if (score >= 60) return 'info';
  return 'secondary';
};

interface CardProps {
  item: FraudItem;
  onUnblock: (userId: string, comment: string) => Promise<void>;
}

const FraudCard: React.FC<CardProps> = ({ item, onUnblock }) => {
  const [showModal, setShowModal] = useState(false);
  const [unblockComment, setUnblockComment] = useState('');
  const [unblocking, setUnblocking] = useState(false);
  const canUnblock = hasAccess('userUnBlock');

  const handleUnblock = async () => {
    setUnblocking(true);
    try {
      await onUnblock(item.userId, unblockComment || 'Снятие блокировки оператором');
      setShowModal(false);
    } finally {
      setUnblocking(false);
    }
  };

  return (
    <>
      <Card className="h-100 shadow-sm border-0" style={{ fontSize: '0.82rem' }}>
        <Card.Body className="pb-2">
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div className="me-2 text-truncate">
              <div className="fw-semibold text-truncate" style={{ fontSize: '0.9rem' }}>
                {item.name}
              </div>
              <div className="text-muted text-truncate" style={{ fontSize: '0.75rem' }}>
                {item.email}
              </div>
            </div>
            <Badge bg="danger" style={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
              Заблокирован
            </Badge>
          </div>

          <div className="mb-2 d-flex flex-wrap gap-1">
            {item.riskScore > 0 && (
              <Badge bg={getRiskColor(item.riskScore)} style={{ fontSize: '0.68rem' }}>
                Риск: {item.riskScore}
              </Badge>
            )}
          </div>

          <div className="row g-1 mb-2" style={{ fontSize: '0.78rem' }}>
            <div className="col-6">
              <span className="text-muted">Телефон:</span>{' '}
              <strong>{item.phone}</strong>
            </div>
            <div className="col-6">
              <span className="text-muted">Страна:</span>{' '}
              <strong>{item.country}</strong>
            </div>
            {item.blockedAt && (
              <div className="col-12">
                <span className="text-muted">Блок:</span>{' '}
                <strong>{new Date(item.blockedAt).toLocaleString('ru-RU')}</strong>
              </div>
            )}
          </div>

          <div className="text-muted mb-2" style={{ fontSize: '0.75rem' }}>
            <strong>Причина:</strong> {item.reason}
          </div>
        </Card.Body>
        <Card.Footer className="bg-transparent pt-0 pb-2 px-3">
          <div className="d-flex justify-content-between align-items-center">
            <code style={{ fontSize: '0.7rem' }} className="text-muted text-truncate">
              {item.userId.slice(0, 8)}…
            </code>
            <Button variant="outline-primary" size="sm" onClick={() => setShowModal(true)}>
              Подробнее
            </Button>
          </div>
        </Card.Footer>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">
            {item.name} <span className="text-muted fw-normal">— {item.userId}</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={6}>
              <div className="text-muted small mb-1">Пользователь</div>
              <div className="fw-semibold">{item.name}</div>
              <div className="text-muted" style={{ fontSize: '0.8rem' }}>{item.email}</div>
              <div className="text-muted" style={{ fontSize: '0.8rem' }}>{item.phone}</div>
              <div className="text-muted" style={{ fontSize: '0.8rem' }}>Страна: {item.country}</div>
            </Col>
            <Col md={6}>
              <div className="text-muted small mb-1">Статус и риск</div>
              <div className="d-flex gap-2 flex-wrap mb-2">
                <Badge bg="danger">Заблокирован</Badge>
                {item.riskScore > 0 && (
                  <Badge bg={getRiskColor(item.riskScore)}>Риск: {item.riskScore}</Badge>
                )}
              </div>
              {item.blockedAt && (
                <div className="small text-muted">
                  Блок: {new Date(item.blockedAt).toLocaleString('ru-RU')}
                </div>
              )}
            </Col>
            <Col md={12}>
              <div className="text-muted small mb-1">Причина блокировки</div>
              <div>{item.reason}</div>
            </Col>
            {canUnblock && (
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="small">Комментарий разблокировки</Form.Label>
                  <Form.Control
                    size="sm"
                    placeholder="Опционально — будет в audit log"
                    value={unblockComment}
                    onChange={e => setUnblockComment(e.target.value)}
                  />
                </Form.Group>
              </Col>
            )}
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={() => setShowModal(false)}>
            Закрыть
          </Button>
          {canUnblock && (
            <Button
              variant="primary"
              size="sm"
              disabled={unblocking || !item.userId}
              onClick={handleUnblock}
            >
              {unblocking ? <Spinner animation="border" size="sm" /> : 'Снять блокировку'}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

interface ListResp {
  data?: RawUser[];
  total?: number;
}

const Fraud: React.FC = () => {
  const [items, setItems] = useState<FraudItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [total, setTotal] = useState(0);

  const fetchBlocked = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await ApiService.get<ListResp>('/user/v1/list', {
        params: { status: 'blocked', size: 200 }
      });
      const list = Array.isArray(data?.data) ? data.data : [];
      setItems(list.map(mapUser).filter(i => i.userId));
      setTotal(typeof data?.total === 'number' ? data.total : list.length);
    } catch (e) {
      const err = e as { message?: string; response?: { data?: { message?: string } } };
      setError(err?.response?.data?.message || err?.message || 'Не удалось загрузить список');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchBlocked();
  }, [fetchBlocked]);

  const handleUnblock = useCallback(async (userId: string, comment: string) => {
    await ApiService.unblockUser(userId, comment);
    await fetchBlocked();
  }, [fetchBlocked]);

  const stats = useMemo(() => {
    const highRisk = items.filter(i => i.riskScore >= 75).length;
    return { highRisk };
  }, [items]);

  return (
    <>
      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <h3 className="mb-0">Фрод-мониторинг</h3>
              <p className="mt-1 mb-0 text-muted small">
                Заблокированные профили пользователей (источник: <code>/user/v1/list?status=blocked</code>)
              </p>
            </div>
            <div className="d-flex gap-2 flex-wrap align-items-center">
              <Badge bg="dark" style={{ fontSize: '0.78rem', padding: '5px 10px' }}>
                Всего: {total}
              </Badge>
              {stats.highRisk > 0 && (
                <Badge bg="danger" style={{ fontSize: '0.78rem', padding: '5px 10px' }}>
                  Высокий риск: {stats.highRisk}
                </Badge>
              )}
              <Button variant="outline-secondary" size="sm" onClick={() => void fetchBlocked()} disabled={loading}>
                {loading ? <Spinner animation="border" size="sm" /> : 'Обновить'}
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger" className="py-2 small">
          {error}{' '}
          <Button size="sm" variant="link" className="p-0 align-baseline" onClick={() => void fetchBlocked()}>
            Повторить
          </Button>
        </Alert>
      )}

      {loading && items.length === 0 && (
        <div className="text-center py-5 text-muted">
          <Spinner animation="border" size="sm" className="me-2" />
          Загрузка списка заблокированных…
        </div>
      )}

      {!loading && items.length === 0 && !error && (
        <Card>
          <Card.Body className="text-center text-muted py-5">
            Нет заблокированных пользователей.
          </Card.Body>
        </Card>
      )}

      <Row className="g-3">
        {items.map(item => (
          <Col key={item.userId} xs={12} sm={6} xl={4}>
            <FraudCard item={item} onUnblock={handleUnblock} />
          </Col>
        ))}
      </Row>
    </>
  );
};

export default Fraud;
